
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  reactive,
  ref,
} from "vue";
import { required } from "@vuelidate/validators";
import { MrUiKitModal, MrUiKitButton, MrUiKitForm } from "mr-ui-kit";
import { useI18n } from "vue-i18n";
import * as validators from "@vuelidate/validators";
import i18Validators from "@/utils/i18n-validators";
import { Organization, RouteEntity, TrainerForm } from "@/interfaces";
import FederationService from "@/services/FederationService";
import { useRoute } from "vue-router";
import OrganizationService from "@/services/OrganizationService";
import AddEntitySelect from "@/components/Backoffice/Common/AddEntitySelect.vue";
import FileService from "@/services/FileService";
import TrainerService from "@/services/TrainerService";

export default defineComponent({
  name: "CreateTrainerModal",
  emits: ["submit", "close", "update:modelValue"],
  components: {
    MrUiKitForm,
    MrUiKitModal,
    MrUiKitButton,
    AddEntitySelect,
  },
  props: {
    entity: {
      type: String as PropType<RouteEntity>,
      default: RouteEntity.Federation,
    },
    modelValue: {
      type: Object as PropType<TrainerForm>,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const withI18nMessage = validators.createI18nMessage({ t });
    const required = withI18nMessage(validators.required, {
      messagePath: i18Validators.required,
    });
    const route = useRoute();
    const isMounted = ref<boolean>(false);
    const entityId = ref<number>(-1);
    const showForm = ref(false);
    const currentOrganizations = reactive<Organization[]>([]);
    const trainer = ref<TrainerForm>(
      props.modelValue || {
        id: -1,
        userCredentials: [],
        organizations: [],
        photo: {},
        firstname: "",
        lastname: "",
      }
    );
    const fields = reactive([
      {
        component: "MrUiKitFileUpload",
        name: "photo",
        props: {
          label: "File",
        },
      },
      {
        component: "MrUiKitInput",
        name: "firstname",
        props: {
          label: computed(() => t("common.fields.firstname")),
          labelPosition: "left",
        },
        rules: {
          required,
        },
      },
      {
        component: "MrUiKitInput",
        name: "lastname",
        props: {
          label: computed(() => t("common.fields.lastname")),
          labelPosition: "left",
        },
        rules: {
          required,
        },
      },
      {
        component: "MrUiKitSelect",
        name: "organizations",
        props: {
          labelKey: "fullname",
          label: computed(() => t("common.organizations")),
          labelPosition: "left",
          key: "id",
          multiple: true,
          items: currentOrganizations,
          classes: ["h-25"],
        },
        rules: {
          required,
        },
      },
      {
        component: "MrUiKitSelect",
        name: "userCredentials",
        props: {
          labelKey: "email",
          label: "Account",
          labelPosition: "left",
          key: "id",
          multiple: true,
          chips: true,
          items: [],
          classes: ["h-25"],
        },
      },
    ]);

    onMounted(async () => {
      entityId.value = +route.params.id;
      if (props.entity === RouteEntity.Federation) {
        await loadOrganizations();
      } else if (props.entity === RouteEntity.Organization) {
        await loadOrganizationById();
      }
      isMounted.value = true;
    });

    const loadOrganizations = async () => {
      const loaded = await FederationService.getOrganizations(
        entityId.value,
        locale.value
      );
      currentOrganizations.push(...loaded);
    };

    const loadOrganizationById = async () => {
      const current = await OrganizationService.getById(
        entityId.value,
        locale.value
      );
      currentOrganizations.push(current);
      trainer.value.organizations.push(current);
    };

    const onSubmit = () => {
      emit("submit", trainer.value);
    };

    const onClose = () => {
      emit("close");
    };

    const onUpdateForm = async (event: any) => {
      if (trainer.value == null) return;
      const field: keyof TrainerForm = event.item.name;
      switch (event.type) {
        case "upload":
          const toUpload = trainer.value[field].file;
          if (toUpload) {
            const uploadedFile = await FileService.upload(
              trainer.value[field].file
            );
            trainer.value[field].url = uploadedFile.url;
            trainer.value[field].file = uploadedFile;
          }
          break;
      }
    };

    const onCreateTrainer = async ({ entity, show }: any) => {
      showForm.value = false;
      if (entity == null) {
        trainer.value = {
          userCredentials: [],
          organizations: [],
          photo: {},
          firstname: "",
          lastname: "",
          id: -1,
        };
      } else {
        const findTrainer = await TrainerService.getById(
          entity.id,
          locale.value
        );
        trainer.value = {
          id: findTrainer.id,
          userCredentials: findTrainer.userCredentials
            ? findTrainer.userCredentials
            : [],
          organizations: findTrainer.organizations,
          photo: {
            file: findTrainer.photo ? findTrainer.photo : null,
            url: findTrainer.photo ? findTrainer.photo.url : null,
          },
          firstname: findTrainer.firstname,
          lastname: findTrainer.lastname,
        };
      }
      emit("update:modelValue", trainer.value);
      showForm.value = show;
    };

    return {
      t,
      onClose,
      onSubmit,
      onUpdateForm,
      onCreateTrainer,
      showForm,
      trainerEntity: RouteEntity.Trainer,
      trainer,
      fields,
      isMounted,
    };
  },
});
